import React, { useEffect, useState, Suspense } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/useAuth';
import { LoadingState } from '../../../shared/components/ui/LoadingState';
import { ErrorBoundary } from '../../../shared/components/ErrorBoundary';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
  requiredOperatorType?: string;
  requireVerification?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredOperatorType,
  requireVerification = false,
}) => {
  const { user, isAuthenticated, loading, redirectToLogin } = useAuth();
  const location = useLocation();
  const [redirectInitiated, setRedirectInitiated] = useState(false);

  useEffect(() => {
    // Only redirect if we're not loading and not authenticated
    if (!loading && !isAuthenticated && !redirectInitiated) {
      setRedirectInitiated(true);
      redirectToLogin('Please sign in to access this page', location.pathname);
    }
  }, [
    loading,
    isAuthenticated,
    redirectInitiated,
    redirectToLogin,
    location.pathname,
  ]);

  // Show loading state while checking auth
  if (loading) {
    return <LoadingState message="Checking authentication..." />;
  }

  // If not authenticated, render nothing while the popup/redirect happens
  if (!isAuthenticated) {
    return null;
  }

  // Check role if required
  if (requiredRole && user?.type !== requiredRole) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check operator type if required
  if (requiredOperatorType && user?.operator_type !== requiredOperatorType) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check verification if required
  if (requireVerification && !user?.is_verified) {
    return <Navigate to="/verification-required" replace />;
  }

  // If all checks pass, render the protected content
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingState message="Loading page..." />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
};
